import { call, put, takeEvery, select } from 'redux-saga/effects';
import { getType } from 'typesafe-actions';

import { RootState } from '../reducers/rootReducer';
import { getRecallOrders, setOrders } from '../actions/orders.actions';
import { setLoading } from '../actions/loading.actions';
import { mapGetOrdersResponseToSetOrdersPayload } from './getOrders.mappers';

import {
  SAGA_GET_RECALL_ORDERS,
  BFF_V2,
  BFF_ORDERS,
  BFF_STORES,
  BFF_RECALL_ORDERS_QUERY_PARAM,
  VERIFIED_RECALLS,
  RECALL_ERROR,
  SORT_ORDERS,
  PICKING_APIGEE_BASE_PATH,
} from '../../constants';

import { GetRecallOrdersPayload } from '../actions/orders.types';
import { OrdersState } from '../reducers/types';
import { APIError, ConfigAuthValues, Order, RecallOrderLocalStorage } from '../../types';

import { callAPI, isToday, stripLeadingZeroes } from '../../utils';
import { handleApiErrors } from './utils';
import { setRecallData } from '../../recallStorageUtils';
import { APIPickTour } from './types';

const callGetRecallOrders = (orderCallData: GetRecallOrdersPayload, configAuthValues: ConfigAuthValues) =>
  callAPI({
    method: 'GET',
    url: `${configAuthValues.APIGEE_BASE_URL}${PICKING_APIGEE_BASE_PATH}${BFF_V2}${BFF_STORES}/${orderCallData.fulfillmentStoreID}${BFF_ORDERS}${BFF_RECALL_ORDERS_QUERY_PARAM}`,
  });

function putRecallOrdersInStore(storeNumber: string, orders: Order[], pickTours: APIPickTour[], ordersState: OrdersState) {
  return setOrders({
    ordersByType: {
      ...ordersState.ordersByType,
      RECALL: {
        orders,
        pickTours,
        sortOrder: SORT_ORDERS.RECALL,
      },
    },
  });
}

export function* runGetRecallOrdersSaga(action: { type: string; payload: GetRecallOrdersPayload }): Generator<any, void, any> {
  yield put(setLoading({ sagaName: SAGA_GET_RECALL_ORDERS, isLoading: true }));

  const configAuthValues: ConfigAuthValues = yield select((state: RootState) => state.config.authValues);
  const ordersState: OrdersState = yield select((state: RootState) => state.orders);
  const storeNumber = stripLeadingZeroes(action.payload.fulfillmentStoreID);
  const localStorageData = localStorage.getItem('storeRecallData');

  try {
    const localStorageRecallData: RecallOrderLocalStorage = localStorageData && JSON.parse(localStorageData);
    const currentStoreRecallData = localStorageRecallData?.[storeNumber];
    const isLocalRecallFresh = currentStoreRecallData && isToday(currentStoreRecallData.receivedDate);

    if (isLocalRecallFresh) {
      const recallOrders = currentStoreRecallData?.recallOrders || [];
      if (recallOrders.length > 0) {
        const pickTours = ordersState.ordersByType.RECALL?.pickTours || [];
        yield put(putRecallOrdersInStore(storeNumber, recallOrders, pickTours, ordersState));
      }
    } else {
      let result;
      const cachedRecalls = localStorage.getItem(VERIFIED_RECALLS);

      if (cachedRecalls) {
        result = { data: JSON.parse(cachedRecalls) };
        localStorage.removeItem(VERIFIED_RECALLS);
      } else {
        result = yield call(
          callGetRecallOrders,
          { fulfillmentStoreID: action.payload.fulfillmentStoreID },
          configAuthValues
        );
      }

      const newPayload = mapGetOrdersResponseToSetOrdersPayload(result.data);
      const recallOrders = newPayload.ordersByType.RECALL?.orders || [];
      const recallPickTours = ordersState.ordersByType.RECALL?.pickTours || [];

      yield setRecallData(action.payload.fulfillmentStoreID, recallOrders);

      if (recallOrders.length > 0 || recallPickTours.length > 0) {
        yield put(putRecallOrdersInStore(storeNumber, recallOrders, recallPickTours, ordersState));
      }

      if (action.payload.setOrders) {
        const storeRecallData = JSON.parse(localStorage.getItem('storeRecallData') || '{}');
        const storedRecallOrders = storeRecallData[storeNumber]?.recallOrders || [];
        if (storedRecallOrders.length > 0) {
          yield put(putRecallOrdersInStore(storeNumber, storedRecallOrders, [], ordersState));
        }
      }

      const errors = result.data.errors || [];
      if (errors.length > 0) {
        const recallError: APIError[] = [
          { id: '', code: RECALL_ERROR, detail: JSON.stringify(result.data.error || errors) },
        ];
        yield handleApiErrors(recallError);
      }
    }
  } catch (error) {
    const recallError: APIError[] = [{ id: '', code: RECALL_ERROR, detail: JSON.stringify(error) }];
    yield handleApiErrors(recallError);
  }

  yield put(setLoading({ sagaName: SAGA_GET_RECALL_ORDERS, isLoading: false }));
}

export default function* watchGetRecallOrders() {
  yield takeEvery(getType(getRecallOrders), runGetRecallOrdersSaga);
}
